import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button, DataTable, LoadingSpinner, SearchInput, Select, useToastActions, useDataTableUrl } from '@airclou/ui';
import { UserPlus, X, RefreshCw, Pencil, Trash2 } from 'lucide-react';
import { listCompanyMembers, listInvitations, resendInvitation, cancelInvitation, removeCompanyMember } from '@/api/groups';
import type { CompanyMember, CompanyInvitation } from '@/api/groups';
import { InviteMemberModal } from './InviteMemberModal';
import { EditMemberModal } from './EditMemberModal';
import { DeleteMemberModal } from './DeleteMemberModal';
import { ErrorState } from '../state/ErrorState';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../state/RoleUnknownNotice';

interface CompanyMembersTabProps {
  companyId: string;
}

type MemberOrInvitation =
  | { type: 'member'; data: CompanyMember }
  | { type: 'invitation'; data: CompanyInvitation };

// The status Select's options ('' = all). A status read from the URL is checked
// against this same list, so the two can't drift apart.
const STATUS_FILTERS = [
  { value: '', labelKey: 'people.allStatuses' },
  { value: 'active', labelKey: 'common.active' },
  { value: 'pending', labelKey: 'common.pending' },
  { value: 'inactive', labelKey: 'common.inactive' },
];

export function CompanyMembersTab({ companyId }: CompanyMembersTabProps) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { success, error: showError } = useToastActions();
  const { user } = useAuth();

  // Every write behind these buttons — invite, resend, cancel, edit, remove —
  // is `TENANT_ROLE_SETS.MANAGE` on the API as of 2026-09-13. `canManageOrg`
  // mirrors that set, so offering the button on any other rule would offer an
  // action the server refuses (role plan §6.4). Reading `orgRole` and not
  // `role`: `role` is the dashboard app-access role, a vocabulary that cannot
  // say 'owner'. Unknown role reads as "cannot" — the same way the sidebar
  // already treats it, so a viewer never sees these flash in.
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<CompanyMember | null>(null);
  const [deletingMember, setDeletingMember] = useState<CompanyMember | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  // Search (and sort, should a column become sortable) live in the URL, same
  // pattern as the audit log, so they survive a reload and back/forward.
  const { search: searchQuery, onSearchChange, sorting, onSortingChange, setParams } = useDataTableUrl();
  // The status filter sits in the URL beside the search; '' (all) is the absent
  // param, and so is a value the Select doesn't offer (a stale or hand-edited
  // link), which would otherwise filter the list with nothing on screen saying so.
  const [searchParams] = useSearchParams();
  const rawStatus = searchParams.get('status') || '';
  const statusFilter = STATUS_FILTERS.some((o) => o.value === rawStatus) ? rawStatus : '';
  const setStatusFilter = useCallback((value: string) => {
    setParams({ status: value || null });
  }, [setParams]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, totalPages: 0 });
  // A ref, not state: flipping it must not change loadData's identity, or the
  // first load would retrigger the effect and run a second fetch behind an overlay.
  const initialLoadDone = useRef(false);

  // Which search is the current one. `SearchInput` debounces, so a request per
  // keystroke this is not — but a slow answer can still be overtaken by a later
  // query's, and the rows now stay on screen under a dim overlay while one is
  // in flight, so adopting it would leave rows — and page meta — for a search the operator has
  // already moved past, with nothing on screen to say so.
  const loadSeq = useRef(0);

  const loadData = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      // Only show full loading spinner on initial load
      if (!initialLoadDone.current) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      setError(null);
      const [membersRes, invitationsRes] = await Promise.all([
        listCompanyMembers(companyId, {
          search: searchQuery || undefined,
          status: statusFilter === 'pending' ? undefined : statusFilter || undefined,
          page: pagination.page,
          limit: pagination.limit,
        }),
        listInvitations(companyId, 'pending'),
      ]);
      if (seq !== loadSeq.current) return;
      setMembers(membersRes.data);
      setPagination(membersRes.meta);
      setInvitations(invitationsRes.invitations);
      initialLoadDone.current = true;
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      setError(err.message || t('people.failedToLoadMembers'));
    } finally {
      if (seq === loadSeq.current) {
        setLoading(false);
        setTableLoading(false);
      }
    }
  }, [companyId, searchQuery, statusFilter, pagination.page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResend = async (invitationId: string) => {
    setResendingId(invitationId);
    try {
      await resendInvitation(companyId, invitationId);
      success(t('people.invitationResent'));
    } catch (err: any) {
      showError(err.message || t('people.failedToResend'));
    } finally {
      setResendingId(null);
    }
  };

  const handleCancel = async (invitationId: string) => {
    setCancellingId(invitationId);
    try {
      await cancelInvitation(companyId, invitationId);
      success(t('people.invitationCancelled'));
      loadData();
    } catch (err: any) {
      showError(err.message || t('people.failedToCancel'));
    } finally {
      setCancellingId(null);
    }
  };

  const handleRemoveMember = async (member: CompanyMember) => {
    try {
      await removeCompanyMember(companyId, member.user_id);
      success(t('people.memberRemoved'));
      setDeletingMember(null);
      loadData();
    } catch (err: any) {
      showError(err.message || t('people.failedToRemove'));
    }
  };

  // Merge members and pending invitations into a single list
  const combinedRows: MemberOrInvitation[] = [
    ...members.map(m => ({ type: 'member' as const, data: m })),
    ...(statusFilter === '' || statusFilter === 'pending'
      ? invitations.map(i => ({ type: 'invitation' as const, data: i }))
      : []),
  ];

  const columns: any[] = [
    {
      id: 'name',
      header: t('common.name'),
      size: 280,
      isFlexible: true,
      cell: (row: MemberOrInvitation) => {
        if (row.type === 'member') {
          return (
            <div>
              <div className="font-medium text-foreground">{row.data.name}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{row.data.email}</div>
            </div>
          );
        }
        return (
          <div>
            <div className="font-medium text-gray-500 dark:text-gray-400">{row.data.email}</div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {t('people.invited')} {formatDateInTz(row.data.created_at, timezone)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'role',
      header: t('common.role'),
      size: 90,
      cell: (row: MemberOrInvitation) => {
        const role = row.type === 'member' ? row.data.company_role : row.data.role;
        return (
          <span className="text-sm text-gray-700 dark:text-gray-200">
            {role === 'admin' ? t('common.admin') : t('common.member')}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: t('common.status'),
      size: 90,
      cell: (row: MemberOrInvitation) => {
        if (row.type === 'invitation') {
          return (
            <span className="px-2 py-1 rounded text-xs border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200">
              {t('common.pending')}
            </span>
          );
        }
        const status = row.data.status;
        return (
          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      id: 'invited',
      header: t('people.invited'),
      size: 130,
      accessorFn: (row: MemberOrInvitation) => {
        const raw = row.type === 'invitation' ? row.data.created_at : (row.data as any).created_at;
        const ts = raw ? new Date(raw).getTime() : 0;
        return Number.isFinite(ts) ? ts : 0;
      },
      sortingFn: 'basic',
      cell: (row: MemberOrInvitation) => {
        const raw = row.type === 'invitation' ? row.data.created_at : (row.data as any).created_at;
        return (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {formatDateInTz(raw, timezone)}
          </span>
        );
      },
    },
    // The whole column goes, rather than rendering empty cells: a column
    // reserves its width whether or not it has anything in it, so leaving it in
    // place would give a read-only viewer a blank 180px gutter with a blank
    // header over it.
    ...(canManage ? [{
      id: 'actions',
      header: '',
      size: 180,
      cell: (row: MemberOrInvitation) => {
        if (row.type === 'invitation') {
          const inv = row.data;
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleResend(inv.id)}
                disabled={resendingId === inv.id}
                title={t('people.resendInvitation')}
              >
                {resendingId === inv.id ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCancel(inv.id)}
                disabled={cancellingId === inv.id}
                className="text-red-600 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                title={t('people.cancelInvitation')}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        }

        const member = row.data;
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingMember(member)}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeletingMember(member)}
              className="text-red-600 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      },
    }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('common.members')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('people.membersDescription')}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setIsInviteModalOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            {t('people.inviteMember')}
          </Button>
        )}
      </div>

      <RoleUnknownNotice />

      <div className="flex items-center gap-3">
        <SearchInput
          value={searchQuery}
          onChange={onSearchChange}
          onClear={() => onSearchChange('')}
          placeholder={t('people.searchMembers')}
          className="flex-1 max-w-sm"
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTERS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          placeholder={t('people.allStatuses')}
        />
      </div>

      {error ? (
        <ErrorState title={t('people.failedToLoadMembers')} description={error} onRetry={loadData} />
      ) : combinedRows.length === 0 && !loading && !tableLoading ? (
        <div className="p-12 text-center">
          <UserPlus className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('people.noMembersYet')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('people.inviteHelp')}
          </p>
          {canManage && (
            <Button onClick={() => setIsInviteModalOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              {t('people.inviteMember')}
            </Button>
          )}
        </div>
      ) : (
        <div className="relative">
          {tableLoading && (
            <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10">
              <LoadingSpinner />
            </div>
          )}
          <DataTable
            data={combinedRows}
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            isLoading={loading}
          />
        </div>
      )}

      {isInviteModalOpen && (
        <InviteMemberModal
          companyId={companyId}
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onSuccess={() => {
            setIsInviteModalOpen(false);
            loadData();
          }}
        />
      )}

      {editingMember && (
        <EditMemberModal
          companyId={companyId}
          member={editingMember}
          isOpen={!!editingMember}
          onClose={() => setEditingMember(null)}
          onSuccess={() => {
            setEditingMember(null);
            loadData();
          }}
        />
      )}

      {deletingMember && (
        <DeleteMemberModal
          member={deletingMember}
          isOpen={!!deletingMember}
          onClose={() => setDeletingMember(null)}
          onConfirm={() => handleRemoveMember(deletingMember)}
        />
      )}
    </div>
  );
}
